import React, { useState, useEffect } from 'react';
import { Sponsor } from '../types';
import { getSponsors, saveSponsor, deleteSponsor } from '../services/db';
import { X, Check, Trash2, User as UserIcon, Loader2, UserPlus, Mail, Phone, MessageCircle, Pencil, Save, MapPin } from 'lucide-react';

interface SponsorManagementProps {
  onClose: () => void;
  onChanged?: () => void;
}

export const SponsorManagement: React.FC<SponsorManagementProps> = ({ onClose, onChanged }) => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Create Sponsor State
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [createError, setCreateError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const loadSponsors = async () => {
    setLoading(true);
    const data = await getSponsors();
    setSponsors(data);
    setLoading(false);
  };

  useEffect(() => {
    loadSponsors();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      setCreateError('Name ist erforderlich');
      return;
    }

    setIsSubmitting(true);
    setCreateError('');

    try {
      const newSponsor: Sponsor = {
        id: Date.now().toString(),
        name: newName.trim(),
        address: newAddress.trim() || undefined,
        email: newEmail.trim() || undefined,
        phone: newPhone.trim() || undefined,
        notes: newNotes.trim() || undefined,
      };

      await saveSponsor(newSponsor);
      await loadSponsors();
      onChanged?.();

      // Reset form
      setNewName('');
      setNewAddress('');
      setNewEmail('');
      setNewPhone('');
      setNewNotes('');
      setIsCreating(false);
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (sponsor: Sponsor) => {
    setEditingId(sponsor.id);
    setEditName(sponsor.name);
    setEditAddress(sponsor.address || '');
    setEditEmail(sponsor.email || '');
    setEditPhone(sponsor.phone || '');
    setEditNotes(sponsor.notes || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditAddress('');
    setEditEmail('');
    setEditPhone('');
    setEditNotes('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editName.trim() || !editingId) return;

    try {
      const updatedSponsor: Sponsor = {
        id: editingId,
        name: editName.trim(),
        address: editAddress.trim() || undefined,
        email: editEmail.trim() || undefined,
        phone: editPhone.trim() || undefined,
        notes: editNotes.trim() || undefined,
      };

      await saveSponsor(updatedSponsor);
      await loadSponsors();
      onChanged?.();
      cancelEdit();
    } catch (e: any) {
      alert('Fehler beim Speichern: ' + e.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Paten unwiderruflich löschen?')) return;

    try {
      await deleteSponsor(id);
      await loadSponsors();
      onChanged?.();
    } catch (e: any) {
      alert('Fehler beim Löschen: ' + e.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Paten-Verwaltung</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              {/* Create Form */}
              {isCreating ? (
                <form onSubmit={handleCreate} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Neuen Paten hinzufügen</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Vollständiger Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input
                        type="text"
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Straße, PLZ Ort"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="email@beispiel.de"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                      <input
                        type="tel"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="+49 123 456789"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
                    <textarea
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Zusätzliche Informationen..."
                    />
                  </div>
                  {createError && (
                    <div className="mt-4 text-red-600 text-sm">{createError}</div>
                  )}
                  <div className="mt-4 flex gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsCreating(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mb-6">
                  <button
                    onClick={() => setIsCreating(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Neuen Paten hinzufügen
                  </button>
                </div>
              )}

              {/* Sponsors List */}
              <div className="space-y-4">
                {sponsors.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Noch keine Paten vorhanden
                  </div>
                ) : (
                  sponsors.map((sponsor) => (
                    <div key={sponsor.id} className="border border-gray-200 rounded-lg p-4">
                      {editingId === sponsor.id ? (
                        <form onSubmit={handleEdit} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                              <input
                                type="text"
                                required
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                              <input
                                type="text"
                                value={editAddress}
                                onChange={(e) => setEditAddress(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                              <input
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                              <input
                                type="tel"
                                value={editPhone}
                                onChange={(e) => setEditPhone(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
                            <textarea
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                            >
                              <Check className="h-4 w-4" />
                              Speichern
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div>
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="text-lg font-semibold text-gray-800">{sponsor.name}</h3>
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEdit(sponsor)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Bearbeiten"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(sponsor.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {sponsor.address && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-gray-400" />
                                <span>{sponsor.address}</span>
                              </div>
                            )}
                            {sponsor.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span>{sponsor.email}</span>
                              </div>
                            )}
                            {sponsor.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-gray-400" />
                                <span>{sponsor.phone}</span>
                              </div>
                            )}
                          </div>
                          {sponsor.notes && (
                            <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                              <div className="flex items-start gap-2">
                                <MessageCircle className="h-4 w-4 text-gray-400 mt-0.5" />
                                <span>{sponsor.notes}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};